import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Maximize2, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAssistant } from "@/context/AssistantContext";
import { useWorkspaceChat } from "@/hooks/use-workspace-chat";
import { Thread } from "@/components/ai/Thread";
import { Composer } from "@/components/ai/Composer";
import { LensMark } from "@/components/brand/ProximaLogo";

const PORTFOLIO_STARTERS = ["Oppsummer status for porteføljen akkurat nå", "Hvilke kraftverk bør vi følge opp denne uken, og hvorfor?", "Sammenlign produksjonen siste 7 dager med uken før", "Hvilke hendelser har gitt produksjonsstans siste 30 dager?"];
const plantStarters = (name) => [`Hva har endret seg ved ${name} siste uke?`, `Vis produksjon, vannføring og spotpris for ${name} siste 14 dager`, `Hvilke stansperioder har ${name} hatt siste 30 dager?`, `Hvordan ligger ${name} an mot årsestimatet?`];

/**
 * "Spør HydroHub" – right-side panel. Same engine as the AI Workspace page, opened in place so the user
 * keeps list/filters/position. Context (plant, period, event) is shown explicitly and sent with the question.
 */
export const AskPanel = () => {
  const { open, setOpen, pending, setPending, plantContext } = useAssistant();
  const chat = useWorkspaceChat();
  const [ctx, setCtx] = useState(null); // { plantId, plantName, label }
  const handled = useRef(null);

  // Hand-over from contextual entry points: set context, optionally auto-send the question.
  useEffect(() => {
    if (!open || !pending || handled.current === pending) return;
    handled.current = pending;
    const next = { plantId: pending.plantId || plantContext?.id || null, plantName: pending.plantName || plantContext?.name || null, label: pending.label || pending.plantName || plantContext?.name || null };
    setCtx(next);
    if (pending.newConversation) chat.reset();
    if (pending.question) {
      chat.send(pending.question, { plantId: next.plantId });
    }
    setPending(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pending]);

  // Default context = the plant currently viewed (if no explicit context).
  useEffect(() => {
    if (!ctx && plantContext) setCtx({ plantId: plantContext.id, plantName: plantContext.name, label: plantContext.name });
    if (ctx && !pending && plantContext && ctx.plantId !== plantContext.id && !chat.messages.length) setCtx({ plantId: plantContext.id, plantName: plantContext.name, label: plantContext.name });
    if (ctx && !plantContext && !chat.messages.length && !pending) setCtx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantContext]);

  const starters = useMemo(() => (ctx?.plantName ? plantStarters(ctx.plantName.replace(/ kraftverk$/i, "")) : PORTFOLIO_STARTERS), [ctx?.plantName]);

  const newConversation = () => {
    chat.reset();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]" data-testid="ask-panel">
        <SheetHeader className="space-y-0 border-b border-border px-5 py-3.5 pr-14">
          <div className="flex items-center gap-3">
            <LensMark className="h-5 w-5" />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-[16px] font-semibold">Spør HydroHub</SheetTitle>
              <SheetDescription className="truncate text-[12.5px]" data-testid="ask-panel-context">
                {ctx?.label ? ctx.label : "Porteføljen"} · svar bygger på målinger og hendelser i HydroHub
              </SheetDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="quiet" size="icon-sm" onClick={newConversation} aria-label="Ny samtale" title="Ny samtale" data-testid="ask-panel-new-button">
                <Plus className="h-4 w-4" />
              </Button>
              <Button asChild variant="quiet" size="icon-sm" aria-label="Åpne i arbeidsflaten" title="Åpne i arbeidsflaten">
                <Link to={chat.conversationId ? `/ai?c=${chat.conversationId}` : "/ai"} onClick={() => setOpen(false)} data-testid="ask-panel-expand-link">
                  <Maximize2 className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5" data-testid="ask-panel-body">
          {chat.messages.length ? (
            <Thread messages={chat.messages} compact />
          ) : (
            <div className="py-6">
              <p className="text-[15px] leading-relaxed text-muted-foreground">Still et spørsmål om {ctx?.plantName ? <span className="font-medium text-foreground">{ctx.plantName}</span> : "porteføljen"} – eller om hva som helst. Svar som gjelder anleggene bygger på faktiske målinger og hendelser, og skiller mellom det som er dokumentert og mulige forklaringer.</p>
              <ul className="mt-5 divide-y divide-border border-y border-border">
                {starters.map((s) => (
                  <li key={s}>
                    <button type="button" onClick={() => chat.send(s, { plantId: ctx?.plantId || null })} className="flex w-full items-center justify-between gap-3 py-3 text-left text-[14.5px] text-foreground transition-colors hover:text-primary" data-testid="ask-panel-starter">
                      <span>{s}</span>
                      <span aria-hidden="true" className="text-muted-foreground">→</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {chat.error ? <p className="py-3 text-[13.5px] text-rose-700">{chat.error}</p> : null}
        </div>

        <div className="border-t border-border bg-background p-3">
          <Composer compact onSend={(t) => chat.send(t, { plantId: ctx?.plantId || null })} onStop={chat.stop} sending={chat.sending} contextLabel={ctx?.label} onClearContext={ctx ? () => setCtx(null) : undefined} autoFocus={open} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
